import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCloudFunction } from '../hooks/useCloudFunction';
import Button from '../components/Button';
import Card from '../components/Card';
import Alert from '../components/Alert';
import { trackEvent } from '../config/analytics';

// Safe date formatter
const safeFormatDate = (dateValue) => {
  try {
    if (!dateValue) return 'Unknown';
    let date;
    if (dateValue?.seconds) {
      date = new Date(dateValue.seconds * 1000);
    } else if (dateValue?.toDate) {
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      date = new Date(dateValue);
    } else {
      return 'Unknown';
    }
    if (isNaN(date.getTime())) return 'Unknown';
    return date.toLocaleDateString();
  } catch (err) {
    return 'Unknown';
  }
};

const SettingsPage = () => {
  const { currentUser, userData, logout } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Delete account
  const { execute: deleteAccount, loading: deleteLoading } = useCloudFunction('deleteUserAccount');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Export data
  const { execute: exportData, loading: exportLoading } = useCloudFunction('exportUserData');

  useEffect(() => {
    trackEvent('settings_page_viewed');
  }, []);

  const handleExportData = async () => {
    try {
      trackEvent('data_export_requested');
      const result = await exportData();
      
      // Create download
      const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `accessshield-data-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      setSuccess('Your data has been exported successfully');
      trackEvent('data_export_completed');
    } catch (err) {
      setError('Failed to export data. Please try again.');
      trackEvent('data_export_error', { error: err.message });
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    try {
      trackEvent('account_deletion_initiated');
      await deleteAccount();
      await logout();
      navigate('/');
      trackEvent('account_deleted');
    } catch (err) {
      setError('Failed to delete account. Please contact support.');
      trackEvent('account_deletion_error', { error: err.message });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">Manage your account preferences and data</p>
        </div>

        {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
        {error && <Alert type="error" message={error} onClose={() => setError('')} />}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <Card className="lg:col-span-1 h-fit">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'profile'
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Profile
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'notifications'
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Notifications
              </button>
              <button
                onClick={() => setActiveTab('data')}
                className={`w-full text-left px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'data'
                    ? 'bg-indigo-50 text-indigo-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                Data & Privacy
              </button>
            </nav>
          </Card>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {activeTab === 'profile' && (
              <>
                <Card>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Information</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Name
                      </label>
                      <p className="text-gray-900">{userData?.displayName || 'Not set'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <p className="text-gray-900">{currentUser?.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account Created
                      </label>
                      <p className="text-gray-900">
                        {safeFormatDate(userData?.createdAt)}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Subscription</h2>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Plan
                      </label>
                      <p className="text-gray-900 capitalize">
                        {userData?.subscriptionTier || 'No active subscription'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <p className="text-gray-900 capitalize">
                        {userData?.subscriptionStatus || 'None'}
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => navigate('/subscription')}
                    >
                      Manage Subscription
                    </Button>
                  </div>
                </Card>
              </>
            )}

            {activeTab === 'notifications' && (
              <Card>
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Email Notifications</h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="audit-complete"
                      defaultChecked
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="audit-complete" className="ml-3 block">
                      <span className="text-gray-900 font-medium">Audit Completion</span>
                      <p className="text-sm text-gray-600">
                        Receive an email when your audit is complete
                      </p>
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="monthly-summary"
                      defaultChecked
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="monthly-summary" className="ml-3 block">
                      <span className="text-gray-900 font-medium">Monthly Summary</span>
                      <p className="text-sm text-gray-600">
                        Monthly summary of your audits and usage
                      </p>
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="usage-alerts"
                      defaultChecked
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="usage-alerts" className="ml-3 block">
                      <span className="text-gray-900 font-medium">Usage Alerts</span>
                      <p className="text-sm text-gray-600">
                        Alert when approaching your audit limit
                      </p>
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="payment-updates"
                      defaultChecked
                      className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor="payment-updates" className="ml-3 block">
                      <span className="text-gray-900 font-medium">Payment Updates</span>
                      <p className="text-sm text-gray-600">
                        Billing and payment notifications
                      </p>
                    </label>
                  </div>

                  <div className="pt-4">
                    <Button>Save Preferences</Button>
                  </div>
                </div>
              </Card>
            )}

            {activeTab === 'data' && (
              <>
                <Card>
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Export Your Data</h2>
                  <p className="text-gray-600 mb-4">
                    Download a copy of all your data including audits, reports, and account information in JSON format.
                  </p>
                  <Button
                    variant="secondary"
                    onClick={handleExportData}
                    loading={exportLoading}
                    disabled={exportLoading}
                  >
                    Export Data
                  </Button>
                </Card>

                <Card className="border-2 border-red-200">
                  <h2 className="text-xl font-semibold text-red-600 mb-6">Delete Account</h2>
                  <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                    <p className="text-red-800 text-sm">
                      <strong>Warning:</strong> This action cannot be undone. All your data,
                      audits, and subscription will be permanently deleted.
                    </p>
                  </div>

                  {!showDeleteConfirm ? (
                    <Button
                      variant="danger"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete Account
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-gray-700">
                        Type <strong>DELETE</strong> to confirm account deletion:
                      </p>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-gray-900"
                        placeholder="Type DELETE"
                      />
                      <div className="flex gap-3">
                        <Button
                          variant="danger"
                          onClick={handleDeleteAccount}
                          loading={deleteLoading}
                          disabled={deleteLoading || deleteConfirmText !== 'DELETE'}
                        >
                          Confirm Deletion
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmText('');
                          }}
                          disabled={deleteLoading}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
